import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useRevalidator } from 'react-router';
import { upsertContactUser } from '../../common/fetch.js';
import { convertToCommonErrors } from '../../common/utils/errorUtils.js';
import { ContactStatus } from '../../generated/api/models.js';
import { getConflictingErrors } from '../../selfservice/epics/authenticatedUserEpic.js';
import { getErrorsFromUpsertContactDialog } from '../UpsertContactDialog/UpsertContactDialog.js';
import type { AddContactDialogParams } from '../../common/types/dialog.js';
import type { CommonError } from '../../common/types/errors.js';
import type {
  CompanyInfoResponse,
  Contact,
  GenericErrorDuplicateContact,
  PutContactResponse,
} from '../../generated/api/models.js';
import type { ReactNode } from 'react';

export type UpsertContactResult =
  | { kind: 'validationError'; errors: CommonError[] }
  | { kind: 'success'; contact: Contact; params?: AddContactDialogParams }
  | { kind: 'possibleDuplicate'; duplicateContact: GenericErrorDuplicateContact }
  | { kind: 'duplicate'; duplicateContact: GenericErrorDuplicateContact }
  | { kind: 'conflict' }
  | { kind: 'error'; errors: CommonError[] };

export interface ContactsEditContextValue {
  saving: boolean;
  editingSection?: string;
  setEditingSection: (section?: string) => void;
  upsertContact: (
    contact: Contact,
    validationErrors?: CommonError[],
    params?: AddContactDialogParams,
    customerType?: CompanyInfoResponse.CustomerTypeEnum,
    mdmId?: string
  ) => Promise<UpsertContactResult>;
}

const ContactsEditContext = createContext<ContactsEditContextValue | undefined>(undefined);

export const useContactsEdit = (): ContactsEditContextValue => {
  const context = useContext(ContactsEditContext);
  if (!context) {
    throw new Error('useContactsEdit must be used within a ContactsEditProvider');
  }
  return context;
};

const classifyUpsertFailure = async (response: Response): Promise<UpsertContactResult> => {
  const body = await response.json().catch(() => undefined);
  const errors = convertToCommonErrors(`ajax error ${response.status}`, response.status, body);
  const duplicateContact = errors[0]?.source?.error?.duplicateContact;

  if (duplicateContact) {
    return errors[0].source?.error?.errorType === 'POSSIBLE_CONTACT_DUPLICATE'
      ? { kind: 'possibleDuplicate', duplicateContact }
      : { kind: 'duplicate', duplicateContact };
  }
  if (getConflictingErrors(errors)) {
    return { kind: 'conflict' };
  }
  return { kind: 'error', errors };
};

export const ContactsEditProvider = ({ children }: { children: ReactNode }) => {
  const { revalidate } = useRevalidator();
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState<string | undefined>(undefined);

  const upsertContact = useCallback(
    async (
      contact: Contact,
      validationErrors?: CommonError[],
      params?: AddContactDialogParams,
      customerType?: CompanyInfoResponse.CustomerTypeEnum,
      mdmId?: string
    ): Promise<UpsertContactResult> => {
      const clientErrors = getErrorsFromUpsertContactDialog(contact, customerType, validationErrors);
      if (clientErrors) {
        return { kind: 'validationError', errors: clientErrors };
      }

      setSaving(true);
      const forceUpsert = contact.contactStatus === ContactStatus.PASSIVE;

      try {
        const response = await upsertContactUser(contact, forceUpsert, false, false, mdmId);
        const { contactId, lastModified }: PutContactResponse = await response.json();
        const upsertedContact: Contact = { ...contact, contactId, lastModified };
        revalidate();
        return { kind: 'success', contact: upsertedContact, params };
      } catch (error) {
        if (error instanceof Response) {
          return await classifyUpsertFailure(error);
        }
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [revalidate]
  );

  const value = useMemo<ContactsEditContextValue>(
    () => ({ saving, editingSection, setEditingSection, upsertContact }),
    [saving, editingSection, upsertContact]
  );

  return <ContactsEditContext.Provider value={value}>{children}</ContactsEditContext.Provider>;
};
